import { AfterViewInit, Component, ElementRef, HostListener, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

interface Partner {
  name: string;
  logo: string;
  url?: string;
  /** Logo is light/white on transparent — render on a dark tile so it's visible. */
  dark?: boolean;
}

/**
 * Partner logo strip. Logos live in src/assets/partners/.
 * Only unique.png ships today; the others fall back to a styled wordmark until
 * you drop hlv.png / gmconnect.png / dreamsnmotion.png into that folder.
 */
@Component({
  selector: 'app-landing-partners',
  standalone: true,
  imports: [NgForOf, NgIf, TranslateModule],
  templateUrl: './landing-partners.component.html',
  styleUrls: ['./landing-partners.component.scss'],
})
export class LandingPartnersComponent implements AfterViewInit, OnDestroy {
  readonly partners: Partner[] = [
    { name: 'Unique Personalservice', logo: 'assets/partners/unique.png', url: 'https://www.unique-personal.de' },
    { name: 'HLV International', logo: 'assets/partners/hlv.png', url: 'https://hlvinternational.com' },
    { name: 'GM Connect', logo: 'assets/partners/gmconnect.png', url: 'https://gm-connect.de', dark: true },
    { name: 'Dreams N Motion', logo: 'assets/partners/dreamsnmotion.png', url: 'http://dreamsnmotion.com' },
    { name: 'Millennia Marrakech', logo: 'assets/partners/millenia.png', url: 'https://www.instagram.com/millennia.centre.de.formation/?hl=de' },
  ];

  @ViewChild('marqueeContainer', { static: true }) private marqueeContainerRef!: ElementRef<HTMLDivElement>;
  @ViewChild('track', { static: true }) private trackRef!: ElementRef<HTMLDivElement>;

  /**
   * How many copies of `partners` make up one half of the track. Must be large enough
   * that one half is at least as wide as the visible marquee, otherwise the track
   * (duplicated twice for the seamless -50% loop) never actually spans edge to edge
   * and the animation looks like it starts mid-screen instead of sweeping the full width.
   */
  private repeat = 2;
  private resizeObserver?: ResizeObserver;

  failed: Record<string, boolean> = {};

  constructor(private readonly zone: NgZone) {}

  /** Duplicated list so the marquee can loop seamlessly (translateX -50%). */
  get loop(): Partner[] {
    const half = Array.from({ length: this.repeat }, () => this.partners).flat();
    return [...half, ...half];
  }

  ngAfterViewInit(): void {
    // Let layout/images settle before the first measurement.
    setTimeout(() => this.updateRepeatCount(), 0);

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.zone.run(() => this.updateRepeatCount()));
      this.resizeObserver.observe(this.marqueeContainerRef.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateRepeatCount();
  }

  onError(name: string): void {
    this.failed[name] = true;
  }

  private updateRepeatCount(): void {
    const containerWidth = this.marqueeContainerRef.nativeElement.clientWidth;
    const trackWidth = this.trackRef.nativeElement.scrollWidth;
    if (!containerWidth || !trackWidth) {
      return;
    }

    const singleSetWidth = trackWidth / (this.repeat * 2);
    if (!singleSetWidth) {
      return;
    }

    const needed = Math.max(2, Math.ceil(containerWidth / singleSetWidth) + 1);
    if (needed !== this.repeat) {
      this.repeat = needed;
    }
  }
}